import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Category } from '../../services/categoryService';
import { tourService } from '../../services/tourService';
import { Tour } from '../../types';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getCategorySubtitle } from '../../utils/categoryTitles';
import SafariWheel from './SafariWheel';
import SpinWheelModal from '../SpinWheel/SpinWheelModal';
import { applyFeaturedOrderWithCategory } from '../../utils/applyFeaturedOrder';
import { usePricing } from '../../contexts/PricingContext';
import { filterTours } from '../../utils/filterTours';
import CachedImage from '../Common/CachedImage';
import { TourCardSkeleton } from '../Common/Skeleton';

const FeaturedTours: React.FC = () => {
  const navigate = useNavigate();
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>(() => {
    try {
      const cached = localStorage.getItem('category_images_cache');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  const [categoryTours, setCategoryTours] = useState<Record<string, Tour[]>>({});
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [showWheel, setShowWheel] = useState<string | null>(null);
  const [showSpinModal, setShowSpinModal] = useState(false);
  const { featuredCategories: allCategories, loading: categoriesLoading } = useCategoryOrder();
  const { actualTheme } = useTheme();
  const pricingContext = usePricing();
  const categoriesRef = useRef<string>(''); 
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingImagesRef = useRef(false);

  const categories = useMemo(() => allCategories.slice(0, 3), [allCategories]);
  
  const isDataReady = useMemo(() => {
    return categories.length > 0 && (Object.keys(categoryImages).length > 0 || tourService.hasCache());
  }, [categories, categoryImages]);

  useEffect(() => {
    const handleToursUpdated = () => {
      loadCategoryImages(categories);
    };
    window.addEventListener('toursUpdated', handleToursUpdated);
    window.addEventListener('categoryToursUpdated', handleToursUpdated);
    return () => {
      window.removeEventListener('toursUpdated', handleToursUpdated);
      window.removeEventListener('categoryToursUpdated', handleToursUpdated);
    };
  }, [categories]);

  useEffect(() => {
    if (allCategories.length > 0) {
      const limitedCategories = allCategories.slice(0, 3);
      const categoryIds = limitedCategories.map(c => c.id).join(',');

      if ((categoryIds !== categoriesRef.current || Object.keys(categoryImages).length === 0) && !loadingImagesRef.current) {
        categoriesRef.current = categoryIds;
        loadingImagesRef.current = true;
        loadCategoryImages(limitedCategories).finally(() => {
          loadingImagesRef.current = false;
        });
      }
    }
  }, [allCategories]);

  const loadCategoryImages = async (categories: Category[]) => {
    const images: Record<string, string> = {};

    try {
      let allTours: Tour[];
      try {
        allTours = await tourService.getActiveTours(false);
      } catch (error) {
        allTours = await tourService.getAllTours(false);
      }

      allTours = filterTours(allTours, pricingContext);

      for (const category of categories) {
        let toursInCategory = allTours.filter(tour => {
          const tourCategoryId = (tour as any).categoryId;
          // Prefer categoryId match; fall back to name ONLY when categoryId is absent
          if (tourCategoryId) return tourCategoryId === category.id;
          return tour.category?.toLowerCase() === category.name.toLowerCase();
        });

        toursInCategory = applyFeaturedOrderWithCategory(toursInCategory, category.id);

        if (toursInCategory.length > 0) {
          const firstTour = toursInCategory[0];
          if (firstTour.images && firstTour.images.length > 0 && firstTour.images[0]) {
            images[category.id] = firstTour.images[0];
          }
        }
      }

      setCategoryImages(images);

      const toursMap: Record<string, Tour[]> = {};
      for (const category of categories) {
        const toursInCategory = allTours.filter(tour => {
          const tourCategoryId = (tour as any).categoryId;
          if (tourCategoryId) return tourCategoryId === category.id;
          return tour.category?.toLowerCase() === category.name.toLowerCase();
        });
        toursMap[category.id] = toursInCategory;
      }
      setCategoryTours(toursMap);

      try {
        localStorage.setItem('category_images_cache', JSON.stringify(images));
        localStorage.setItem('category_images_cache_timestamp', Date.now().toString());
      } catch (error) {}
    } catch (error) {}
  };

  const getCategoryImage = (category: Category): string => {
    return categoryImages[category.id] || '';
  };

  if ((categoriesLoading || !isDataReady) && categories.length === 0) {
    return (
      <section className={`py-12 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <TourCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-12 sm:py-20 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 px-4">
          <h2 className={`text-3xl md:text-5xl font-extrabold mb-4 font-serif tracking-tight ${actualTheme === 'dark' ? 'text-slate-100' : 'text-slate-900'
            }`}>
            Signature <span className="text-blue-500">Categories</span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto font-medium ${actualTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'
            }`}>
            Embark on a specialized journey tailored to your interests, from high-altitude trekking to serene coastal escapes.
          </p>
        </div>

        {/* Categories Grid */}
        {categories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const categoryId = category?.id;
              if (!categoryId) return null;

              const targetRoute = `/category/${encodeURIComponent(categoryId)}`;

              const handleMouseEnter = () => {
                const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
                if (isTouchDevice) return;

                setHoveredCategory(categoryId);
                hoverTimeoutRef.current = setTimeout(() => {
                  setShowWheel(categoryId);
                }, 800);
              };

              const handleMouseLeave = () => {
                setHoveredCategory(null);
                setShowWheel(null);
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
              };

              return (
                <div
                  key={categoryId}
                  className="relative group"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={targetRoute}
                    className={`block relative rounded-3xl overflow-hidden transition-all duration-500 border-2 shadow-xl hover:shadow-2xl hover:-translate-y-2 ${actualTheme === 'dark'
                        ? 'bg-slate-800/30 border-slate-700/50'
                        : 'bg-white border-slate-100'
                      }`}
                  >
                    {/* Image Area */}
                    <div className="relative h-64 overflow-hidden">
                      {getCategoryImage(category) ? (
                        <CachedImage
                          src={getCategoryImage(category)}
                          alt={category.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 animate-pulse" />
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                        {/* Label */}
                        <div className="absolute bottom-6 left-6 right-6">
                          <span 
                            className="inline-block bg-primary-900 text-white px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest mb-2 shadow-sm"
                          >
                            {category.name}
                          </span>
                          <h3 className="text-2xl font-extrabold text-white font-serif drop-shadow-md">
                            {category.name}
                          </h3>
                        </div>
                      </div>

                      {/* Content Area */}
                      <div className="p-6">
                        <p className={`text-sm line-clamp-2 font-medium mb-6 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                          {category.description || getCategorySubtitle(category.name)}
                        </p>
                        
                        <div className={`flex items-center justify-between pt-4 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-slate-100'}`}>
                          <span className={`text-xs font-extrabold uppercase tracking-widest ${actualTheme === 'dark' ? 'text-blue-400' : 'text-slate-900'}`}>
                            Explore Expeditions
                          </span>
                          <div className={`${actualTheme === 'dark' ? 'bg-blue-600' : 'bg-slate-900'} text-white p-2 rounded-full shadow-lg group-hover:scale-110 transition-transform`}>
                            <ArrowRightIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                  </Link>

                  {/* Safari Wheel */}
                  {showWheel === categoryId && categoryTours[categoryId] && (
                    <SafariWheel
                      tours={categoryTours[categoryId]}
                      categoryName={category.name}
                      isVisible={true}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-slate-50 dark:bg-slate-800/20 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-700">
            <p className={`font-medium ${actualTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
              Our expedition categories are currently being updated.
            </p>
          </div>
        )}

        {/* Action Hub */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-20 relative z-10 w-full max-w-3xl mx-auto px-4">
          <Link
            to="/categories"
            className={`group flex items-center gap-4 px-10 py-5 rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:-translate-y-1 w-full sm:w-auto justify-center ${actualTheme === 'dark'
                ? 'bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 hover:border-blue-500/30'
                : 'bg-white border border-slate-100 text-slate-900 hover:border-blue-100 hover:bg-slate-50'
                }`}
          >
            <span className="font-extrabold text-xs uppercase tracking-widest">Global Taxonomy</span>
            <div className={`p-1.5 rounded-full transition-all ${actualTheme === 'dark' ? 'bg-slate-700 group-hover:bg-blue-600' : 'bg-blue-50 group-hover:bg-blue-500'}`}>
              <ArrowRightIcon className={`w-4 h-4 transition-all duration-500 group-hover:translate-x-1 ${actualTheme === 'dark' ? 'text-blue-400 group-hover:text-white' : 'text-blue-600 group-hover:text-white'}`} />
            </div>
          </Link>

          <button
            onClick={() => setShowSpinModal(true)}
            className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full overflow-hidden shadow-2xl hover:shadow-blue-500/30 transition-all hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-blue-500 opacity-100 group-hover:opacity-90 transition-opacity" />
            <div className="relative flex items-center gap-2 text-white font-extrabold text-xs uppercase tracking-widest">
              <span>Personalized Curator</span>
            </div>
          </button>
        </div>
      </div>

      <SpinWheelModal
        isOpen={showSpinModal}
        onClose={() => setShowSpinModal(false)}
        mode="categories"
      />
    </section>
  );
};

export default FeaturedTours;